import React from 'react';
import { MenuItem } from '@mui/material';
import { FormInputText } from '../../../form-components/FormInputText';
import { FormInputDropdown } from '../../form/FormInputDropdown';
import { useTranslation } from 'react-i18next';
import { useWatch } from 'react-hook-form';
import CustomRadioBox from '../../CustomRadioBox';

import {
  FormFieldRow,
  FormFieldGroup,
  FormFieldGroupHalf,
  FormFieldLabel,
  FormInputWrapperFull,
  StyledFormControlLabel,
  dropdownStyle,
  textInputStyle,
  DropdownArrowIcon,
  ReceiptTypeTabContainer,
  ReceiptTypeTab,
} from '../../SalesManagment/modal/enterSaleInfoModal/EnterSaleInfoModalStyled';

// 은행 목록
export const BANK_LIST = [
  { value: 'WOORI', label: '우리은행' },
  { value: 'KB', label: 'KB국민은행' },
  { value: 'SHINHAN', label: '신한은행' },
  { value: 'HANA', label: '하나은행' },
  { value: 'NH', label: 'NH농협은행' },
  { value: 'IBK', label: 'IBK기업은행' },
  { value: 'KAKAO', label: '카카오뱅크' },
  { value: 'TOSS', label: '토스뱅크' },
  { value: 'SC', label: 'SC제일은행' },
  { value: 'CITI', label: '씨티은행' },
];

const BankTransferForm = ({ control, setValue, readOnly = false }) => {
  const { t } = useTranslation();

  // form 값을 직접 읽어서 UI에 반영
  const receiptYn = useWatch({ control, name: 'receiptYn' }) || 'Y';
  const deductionTypeValue = useWatch({ control, name: 'deductionType' });
  const deductionType = deductionTypeValue === 1 ? 'business' : 'personal';

  const handleReceiptYnChange = value => {
    setValue('receiptYn', value);
  };

  const handleDeductionTypeChange = value => {
    setValue('deductionType', value === 'personal' ? 0 : 1);
  };

  return (
    <>
      {/* 현금영수증 / 발행구분 — 라디오·탭을 위로 붙이고 아래 전화번호/승인번호 라벨과 간격을 둔다 */}
      <FormFieldRow sx={{ alignItems: 'flex-start', marginBottom: '10px' }}>
        <FormFieldGroupHalf>
          <FormFieldLabel>{t('현금영수증')}</FormFieldLabel>
          <FormInputWrapperFull style={{ height: '36px' }}>
            <StyledFormControlLabel
              control={
                <CustomRadioBox
                  checked={receiptYn === 'Y'}
                  onChange={() => handleReceiptYnChange('Y')}
                  disabled={readOnly}
                />
              }
              label={t('발행')}
            />
            <StyledFormControlLabel
              control={
                <CustomRadioBox
                  checked={receiptYn === 'N'}
                  onChange={() => handleReceiptYnChange('N')}
                  disabled={readOnly}
                />
              }
              label={t('미발행')}
            />
          </FormInputWrapperFull>
        </FormFieldGroupHalf>

        {/* 발행구분 - 발행일 때만 표시 */}
        {receiptYn === 'Y' && (
          <FormFieldGroupHalf>
            <FormFieldLabel>{t('발행구분')}</FormFieldLabel>
            <ReceiptTypeTabContainer>
              <ReceiptTypeTab
                active={deductionType === 'personal'}
                onClick={() => !readOnly && handleDeductionTypeChange('personal')}
                position="left">
                {t('개인소득공제')}
              </ReceiptTypeTab>
              <ReceiptTypeTab
                active={deductionType === 'business'}
                onClick={() => !readOnly && handleDeductionTypeChange('business')}
                position="right">
                {t('사업자지출증빙')}
              </ReceiptTypeTab>
            </ReceiptTypeTabContainer>
          </FormFieldGroupHalf>
        )}

        {/* 미발행이어도 승인번호는 받는다 — 계좌이체는 이체 내역으로 발급·승인번호를 확인할 수 없어
            기록·조회할 자리가 필요하다는 고객사 요청(2026-09-15). 발행구분 자리(현금영수증 오른쪽)에 둔다. */}
        {receiptYn === 'N' && (
          <FormFieldGroupHalf>
            <FormFieldLabel>{t('승인번호')}</FormFieldLabel>
            <FormInputText
              control={control}
              disabled={readOnly}
              color="info"
              sx={textInputStyle}
              variant="outlined"
              name="approvalNumber"
              size="small"
              placeholder={t('승인번호를 입력해 주세요.')}
            />
          </FormFieldGroupHalf>
        )}
      </FormFieldRow>

      {/* 발행일 때만 추가 필드 표시 */}
      {receiptYn === 'Y' && (
        <>
          {/* 전화번호/사업자등록번호 | 승인번호 */}
          <FormFieldRow>
            <FormFieldGroupHalf>
              <FormFieldLabel>
                {deductionType === 'personal' ? t('전화번호') : t('사업자등록번호')}
              </FormFieldLabel>
              <FormInputText
                control={control}
                disabled={readOnly}
                color="info"
                sx={textInputStyle}
                variant="outlined"
                name={deductionType === 'personal' ? 'issuePhoneNumber' : 'issueBusinessNumber'}
                size="small"
                placeholder={
                  deductionType === 'personal'
                    ? t('전화번호를 입력해 주세요.')
                    : t('사업자등록번호를 입력해 주세요.')
                }
              />
            </FormFieldGroupHalf>
            <FormFieldGroupHalf>
              <FormFieldLabel>{t('승인번호')}</FormFieldLabel>
              <FormInputText
                control={control}
                disabled={readOnly}
                color="info"
                sx={textInputStyle}
                variant="outlined"
                name="approvalNumber"
                size="small"
                placeholder={t('승인번호를 입력해 주세요.')}
              />
            </FormFieldGroupHalf>
          </FormFieldRow>

          {/* 입금은행 (full width) */}
          <FormFieldGroup>
            <FormFieldLabel>{t('입금은행')}</FormFieldLabel>
            <FormInputDropdown
              control={control}
              disabled={readOnly}
              sx={dropdownStyle}
              name="bankCode"
              options={BANK_LIST}
              disabledItem={<MenuItem disabled>{t('은행 선택')}</MenuItem>}
              IconComponent={DropdownArrowIcon}
            />
          </FormFieldGroup>

          {/* 계좌번호 | 입금자명 */}
          <FormFieldRow>
            <FormFieldGroupHalf>
              <FormFieldLabel>{t('계좌번호')}</FormFieldLabel>
              <FormInputText
                control={control}
                disabled={readOnly}
                color="info"
                sx={textInputStyle}
                variant="outlined"
                name="bankInfo"
                size="small"
                placeholder={t('계좌번호를 입력해 주세요.')}
              />
            </FormFieldGroupHalf>
            <FormFieldGroupHalf>
              <FormFieldLabel>{t('입금자명')}</FormFieldLabel>
              <FormInputText
                control={control}
                disabled={readOnly}
                color="info"
                sx={textInputStyle}
                variant="outlined"
                name="depositorName"
                size="small"
                placeholder={t('입금자명을 입력해 주세요.')}
              />
            </FormFieldGroupHalf>
          </FormFieldRow>
        </>
      )}
    </>
  );
};

export default BankTransferForm;
